<template>
  <BittsModal
    class="c-new-secret-generated-modal webhook-secret-modal"
    aria-label="New Secret Generated"
    variant="data"
    :width="475"
    :loading="isGeneratingSecret"
    :disabled="isGeneratingSecret"
    primary-button-text="Done"
    @action="handleClose"
    @closed="handleClose"
  >
    <template #header>
      <div>
        <h2 class="text-neutral-text-strong font-bold text-xl pb-8">
          New Secret Generated
        </h2>
      </div>
    </template>
    <div class="p-24">
      <div class="space-y-16">
        <div>
          <label
            for="webhook-secret"
            class="block text-neutral-text-strong font-medium text-sm mb-8"
          >
            Webhook Secret
          </label>
          <BittsInput
            v-if="webhookSecret"
            id="webhook-secret"
            :model-value="webhookSecret || ''"
            name="webhook-secret"
            type="text"
            :read-only="true"
            :allow-copy="true"
          />
        </div>
        <BittsAlert
          color="warning"
          message="Make sure you save this secret key"
          description="You won't be able to see it again."
        />
        <div class="text-neutral-text-weak">
          Reminder: Complete setup (including testing) to continue receiving
          signals
        </div>
      </div>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsAlert, BittsInput, BittsModal } from '@crossbeam/bitts';

import { onMounted, ref } from 'vue';

import { useWebhooksStore } from '@/stores';

const { webhookId } = defineProps<{
  webhookId: string | null;
}>();

const emit = defineEmits<{
  closed: [];
}>();

const webhooksStore = useWebhooksStore();
const isGeneratingSecret = ref(false);
const webhookSecret = ref<string | null>(null);

async function generateSecret() {
  if (!webhookId) return;
  isGeneratingSecret.value = true;

  const secretData = await webhooksStore.createWebhookSecret(webhookId);
  webhookSecret.value = secretData?.client_secret || null;

  isGeneratingSecret.value = false;
}

function handleClose() {
  webhookSecret.value = null;
  emit('closed');
}

onMounted(() => {
  generateSecret();
});
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-new-secret-generated-modal.o-bitts-modal.bitts-modal--data:deep() {
  .o-bitts-modal__content {
    @apply p-0 m-0;
  }

  /* Style the read-only input to look like disabled */
  .c-bitts-input__input.read-only {
    @apply bg-neutral-background-disabled text-neutral-text-weak border-neutral-border outline-0;
  }
  /* Override the default read-only hover/focus effects */
  .c-bitts-input__input.read-only:hover,
  .c-bitts-input__input.read-only:focus,
  .c-bitts-input__input.read-only:focus-within {
    @apply border-neutral-border! outline-0! shadow-none!;
  }
  /* Override the inner input background */
  .c-bitts-input__input.read-only .ant-input {
    @apply bg-neutral-background-disabled!;
  }
  /* Ensure the copy button remains interactive */
  .c-bitts-input__suffix {
    @apply cursor-pointer;
  }

  /* Hide the secondary button */
  .o-bitts-modal__secondary-button {
    @apply hidden;
  }
  /* Move the primary button to the right */
  .o-bitts-modal__footer {
    @apply justify-end;
  }
}

/* Lower the modal z-index so the "copied in clipboard" popovers appear above it */
.c-new-secret-generated-modal.open {
  @apply z-1030!;
}
</style>
